import { useState } from 'react';

import * as MicrosoftGraph from '@microsoft/microsoft-graph-types';
import InfoIcon from '@mui/icons-material/Info';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Box,
    Button,
    CircularProgress,
    Collapse,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableRowProps,
    Typography,
    styled
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import Loadable from '../../../components/common/loadable';
import { UserRoles } from '../../../components/roster/types';
import { useMsTeams } from '../../../providers/MsTeamsContext';
import InviteStudentsContainer from '../InviteStudentsContainer';
import { useMyTeams, useTeamMembers } from '../requests/msTeams';
import { Student } from '../types/inviteStudentsTypes';

const StyledInfoTypography = styled(Typography)`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
` as typeof Typography;

const StyledTable = styled(Table)`
    & .MuiTableRow-root {
        > .MuiTableCell-root:nth-of-type(1) {
            width: 250px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }

        > .MuiTableCell-root:nth-of-type(2) {
            width: auto;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;
        }

        > .MuiTableCell-root:nth-of-type(3) {
            width: 0;
            text-align: center;
        }

        > .MuiTableCell-root:nth-of-type(4) {
            width: 46px;
        }
    }
` as typeof Table;

const TeamRow = styled(TableRow)`
    & .MuiTableCell-root {
        border: none;
    }
` as typeof TableRow;

const TeamHeaderCellContent = styled(Stack)`
    flex-direction: row;
    align-items: center;
    gap: 8px;
    font-weight: 600;
` as typeof Box;

const ErrorMessageWrapper = styled(Stack)`
    flex-direction: column;
    align-items: center;
` as typeof Box;

const Row = (props: TableRowProps & { team: MicrosoftGraph.Team }) => {
    const { team } = props;
    const { msToken } = useMsTeams();
    const { t } = useTranslation('invite_students');

    const [open, setOpen] = useState(false);
    const { addStudent, isStudentAdded } = useContainer(InviteStudentsContainer);

    const handleAddUser = (user: MicrosoftGraph.User) => {
        const student: Student = {
            firstName: user.givenName ?? '',
            lastName: user.surname ?? '',
            email: user.userPrincipalName ?? '',
            role: UserRoles.Student
        };

        addStudent(student);
    };

    const handleAddUsers = (users: MicrosoftGraph.User[]) => {
        users.forEach(handleAddUser);
    };

    const {
        data: teamUsers,
        isFetching,
        isFetched
    } = useTeamMembers({
        teamId: team.id,
        open,
        msToken
    });

    return (
        <>
            <TeamRow key={`team-header-${team.id}`}>
                <TableCell />
                <TableCell>
                    <TeamHeaderCellContent>
                        {team.displayName}
                        {isFetching && <CircularProgress disableShrink size={16} />}
                    </TeamHeaderCellContent>
                </TableCell>
                <TableCell />
                <TableCell sx={{ padding: '6px' }}>
                    <IconButton size="small" onClick={() => setOpen((c) => !c)}>
                        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                </TableCell>
            </TeamRow>

            <TableRow key={`team-body-${team.id}`}>
                <TableCell colSpan={4} sx={{ padding: 0 }}>
                    <Collapse in={open}>
                        {isFetched && teamUsers && (
                            <Table key={team.id} size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>{t('ms-teams.name')}</TableCell>
                                        <TableCell>{t('ms-teams.email')}</TableCell>
                                        <TableCell>
                                            <Button
                                                color="primary"
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleAddUsers(teamUsers)}
                                            >
                                                {t('ms-teams.add-all-members')}
                                            </Button>
                                        </TableCell>
                                        <TableCell />
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {teamUsers.map((user) => {
                                        const isAdded = isStudentAdded(user.userPrincipalName!);

                                        return (
                                            <TableRow key={user.id}>
                                                <TableCell>{user.displayName}</TableCell>
                                                <TableCell>{user.userPrincipalName}</TableCell>
                                                <TableCell>
                                                    {!isAdded && (
                                                        <Button
                                                            color="primary"
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={() => handleAddUser(user)}
                                                        >
                                                            {t('ms-teams.add')}
                                                        </Button>
                                                    )}

                                                    {isAdded && (
                                                        <Button disabled size="small">
                                                            {t('ms-teams.added')}
                                                        </Button>
                                                    )}
                                                </TableCell>
                                                <TableCell />
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        )}
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export const MsTeamsInvite = () => {
    const { msToken } = useMsTeams();
    const { t } = useTranslation('invite_students');

    const { data: teams, isLoading, isFetched, isError } = useMyTeams({ msToken });

    if (isError) {
        return (
            <ErrorMessageWrapper>
                <Typography color="error">{t('errors.error-loading-teams')}</Typography>
            </ErrorMessageWrapper>
        );
    }

    return (
        <>
            {isFetched && (
                <StyledInfoTypography variant="subtitle1">
                    <InfoIcon fontSize="small" color="primary" />
                    {t('ms-teams.adjust-roles')}
                </StyledInfoTypography>
            )}

            <Loadable isLoading={isLoading} title={t('ms-teams.loading-teams')}>
                {isFetched && (!teams || !teams.length) && (
                    <ErrorMessageWrapper>
                        <Typography color="error">{t('errors.not-member-of-any-team')}</Typography>
                    </ErrorMessageWrapper>
                )}

                {isFetched && teams && (
                    <TableContainer component={Paper}>
                        <StyledTable size="small">
                            <TableBody>
                                {Object.values(teams).map((team) => (
                                    <Row key={team.id} team={team} />
                                ))}
                            </TableBody>
                        </StyledTable>
                    </TableContainer>
                )}
            </Loadable>
        </>
    );
};
